import { useSnackbar as useSnackbarNotistack } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type Variant = 'default' | 'error' | 'success' | 'warning' | 'info';

export function useSnackbar() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbarNotistack();

  const handleSnackbar = useCallback(
    (variant: Variant = 'default') =>
      (key: string, dictionary: Record<string, string>) => {
        const translatedDictionary = Object.entries(dictionary).reduce(
          (acc, [tKey, tValue]) => ({ ...acc, [tKey]: t(tValue) }),
          dictionary
        );
        return enqueueSnackbar(t(key, translatedDictionary), { variant });
      },
    [enqueueSnackbar, t]
  );

  const toast = useCallback(
    (key: string, dictionary: Record<string, string> = {}) => handleSnackbar()(key, dictionary),
    [handleSnackbar]
  );

  const toastSuccess = useCallback(
    (key: string, dictionary: Record<string, string> = {}) => handleSnackbar('success')(key, dictionary),
    [handleSnackbar]
  );

  const toastError = useCallback(
    (key: string, dictionary: Record<string, string> = {}) => handleSnackbar('error')(key, dictionary),
    [handleSnackbar]
  );

  const toastWarning = useCallback(
    (key: string, dictionary: Record<string, string> = {}) => handleSnackbar('warning')(key, dictionary),
    [handleSnackbar]
  );

  const toastInfo = useCallback(
    (key: string, dictionary: Record<string, string> = {}) => handleSnackbar('info')(key, dictionary),
    [handleSnackbar]
  );

  return {
    toast,
    toastSuccess,
    toastError,
    toastWarning,
    toastInfo,
  };
}
